import { useState, useEffect } from "react";
import axios from "axios";
import { BRAND } from "@/data/products";
import { Lock, Mail, Phone, MessageSquare, Tag, Calendar, RefreshCw, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = "vardaan2014";
const STORAGE_KEY = "va_admin_ok";

const CATEGORY_LABEL = {
  indoor: "Indoor Temple",
  outdoor: "Outdoor Temple",
  statues: "Statue",
  fountain: "Fountain",
  "wall-panel": "Wall Panel",
  showpiece: "Showpiece",
  inlay: "Inlay Work",
  "wash-basin": "Wash Basin",
  animals: "Elephants & Lions",
  "table-top": "Table Top",
  general: "General",
};

export default function Admin() {
  const [authed, setAuthed] = useState(
    typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "1"
  );
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/inquiries`);
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) fetchInquiries();
  }, [authed]);

  const login = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PASSWORD) {
      window.localStorage.setItem(STORAGE_KEY, "1");
      setAuthed(true);
      setError("");
    } else {
      setError("Wrong password. Please try again.");
    }
  };

  const logout = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPw("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src={BRAND.logo} alt="Vardaan Arts" className="w-20 h-20 mx-auto object-contain" />
            <h1 className="font-serif text-4xl text-ink mt-4">Vardaan Arts</h1>
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold-dark mt-2 font-medium">
              Admin · Inquiries
            </div>
          </div>
          <form onSubmit={login} className="bg-white border border-gold/30 p-8" data-testid="admin-login-form">
            <label className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mb-2 block">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gold-dark absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="admin-password-input"
                placeholder="Enter admin password"
                className="w-full bg-transparent border border-gold/40 focus:border-gold outline-none py-3 pl-10 pr-3 text-ink"
                autoFocus
              />
            </div>
            {error && <div className="text-red-700 text-sm mt-3">{error}</div>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="mt-6 w-full px-6 py-3 bg-ink text-cream-100 text-[12px] tracking-[0.28em] uppercase hover:bg-gold transition-colors duration-500"
            >
              Enter
            </button>
          </form>
          <p className="text-center text-xs text-ink-soft mt-6">
            This page is for Vardaan Arts owner only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100" data-testid="admin-page">
      <header className="bg-ink text-cream-100 border-b border-gold/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Vardaan Arts" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-serif text-xl tracking-wider">VARDAAN ARTS</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-gold-light mt-1">
                Admin · Customer Inquiries
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchInquiries}
              data-testid="admin-refresh-btn"
              className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase border border-gold/40 hover:bg-gold/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase bg-gold text-ink hover:bg-gold-light"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-ink">
              {items.length} {items.length === 1 ? "Inquiry" : "Inquiries"}
            </h2>
            <p className="text-ink-soft text-sm mt-1">Newest first · Auto-saved from your website contact form</p>
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="border border-gold/30 bg-white p-16 text-center">
            <div className="font-serif text-2xl text-ink">No inquiries yet</div>
            <p className="text-ink-soft mt-3 max-w-md mx-auto">
              As soon as a visitor sends an inquiry from your website, it will appear here.
              Refresh this page or reload to see new ones.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((it) => (
            <article
              key={it.id}
              data-testid={`inquiry-card-${it.id}`}
              className="bg-white border border-gold/30 p-6 md:p-7 hover:border-gold transition-colors"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-serif text-2xl text-ink leading-tight">{it.name}</h3>
                  <div className="text-xs text-ink-soft mt-1 flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    {new Date(it.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-cream-200 text-ink text-[10px] tracking-[0.2em] uppercase">
                  <Tag className="w-3 h-3" />
                  {CATEGORY_LABEL[it.category] || it.category}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <a
                  href={`tel:${it.phone}`}
                  className="flex items-center gap-3 text-ink hover:text-gold-dark"
                >
                  <Phone className="w-4 h-4 text-gold-dark" />
                  {it.phone}
                </a>
                <a
                  href={`https://wa.me/${it.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#25D366] hover:underline"
                >
                  <MessageSquare className="w-3 h-3" />
                  Open in WhatsApp
                </a>
                {it.email && (
                  <a
                    href={`mailto:${it.email}`}
                    className="flex items-center gap-3 text-ink hover:text-gold-dark"
                  >
                    <Mail className="w-4 h-4 text-gold-dark" />
                    {it.email}
                  </a>
                )}
              </div>

              {it.message && (
                <div className="mt-4 pt-4 border-t border-gold/20">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mb-2">
                    Message
                  </div>
                  <p className="text-ink text-[15px] leading-relaxed whitespace-pre-wrap">
                    {it.message}
                  </p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
